import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import firebase, { auth, db } from "../firebase/config";
import SignInModal from "../components/SignInModal";

const AuthContext = createContext(null);
const OWNER_EMAIL = "patrickchimtedza@gmail.com";

export function AuthProvider({ children }) {
  const [user, setUser]                       = useState(undefined);
  const [isAdmin, setIsAdmin]                 = useState(false);
  const [isDeliveryStaff, setIsDeliveryStaff] = useState(false);
  const [isEventStaff, setIsEventStaff]       = useState(false);
  const [viewAs, setViewAs]                   = useState(null); // "admin" | "delivery" | "event_staff" | "customer" | "referral"
  const [signInOpen, setSignInOpen]           = useState(false);
  const afterSignInRef                        = useRef(null);

  useEffect(() => auth.onAuthStateChanged((u) => setUser(u || null)), []);

  // Creates/updates the user's Firestore doc after any successful sign-in.
  const finishSignIn = async (u) => {
    const parts   = (u.displayName || "").split(" ");
    const refCode = localStorage.getItem("gvs_ref");
    const docRef  = db.collection("users").doc(u.uid);
    const snap    = await docRef.get();
    if (!snap.exists) {
      await docRef.set({
        firstName: parts[0] || "",
        lastName:  parts.slice(1).join(" ") || "",
        email:     u.email || "",
        phone:     "",
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        ...(refCode ? { referredBy: refCode } : {}),
      });
      if (refCode) {
        const refSnap = await db.collection("referrals").where("code", "==", refCode).get();
        if (!refSnap.empty) {
          refSnap.docs[0].ref.update({
            totalSignups: firebase.firestore.FieldValue.increment(1),
            updatedAt:    firebase.firestore.FieldValue.serverTimestamp(),
          });
        }
        localStorage.removeItem("gvs_ref");
      }
    } else {
      await docRef.update({ email: u.email || "", updatedAt: firebase.firestore.FieldValue.serverTimestamp() });
    }
  };

  // Handle Google redirect result (legacy path — kept for any in-flight redirects).
  useEffect(() => {
    auth.getRedirectResult().then(async (result) => {
      if (!result.user) return;
      await finishSignIn(result.user);
      const afterSignInPath = sessionStorage.getItem("gvs_after_signin_path");
      if (afterSignInPath) {
        sessionStorage.removeItem("gvs_after_signin_path");
        window.location.assign(afterSignInPath);
      }
    }).catch((e) => console.error("Google redirect sign-in failed:", e));
  }, []);

  // Google sign-in via popup — avoids the third-party storage partitioning
  // that breaks signInWithRedirect's result relay on localhost (and in some
  // browsers generally): https://github.com/firebase/firebase-js-sdk/issues
  const signInWithGoogle = async () => {
    const result = await auth.signInWithPopup(new firebase.auth.GoogleAuthProvider());
    await finishSignIn(result.user);
    return result.user;
  };

  const [devSigningIn, setDevSigningIn] = useState(false);
  const isLocalhost = window.location.hostname === "localhost";

  const handleDevSignIn = async () => {
    setDevSigningIn(true);
    try {
      await signInWithGoogle();
    } catch (e) {
      console.error("Dev sign-in failed:", e);
    }
    setDevSigningIn(false);
  };

  useEffect(() => {
    if (!user) { setIsAdmin(false); setIsDeliveryStaff(false); setIsEventStaff(false); setViewAs(null); return; }
    if (user.email === OWNER_EMAIL) { setIsAdmin(true); return; }
    db.collection("admins").where("email", "==", user.email).get()
      .then((snap) => setIsAdmin(!snap.empty))
      .catch(() => setIsAdmin(false));
    db.collection("delivery_staff").where("userId", "==", user.uid).get()
      .then((snap) => setIsDeliveryStaff(!snap.empty))
      .catch(() => setIsDeliveryStaff(false));
    db.collection("event_staff").where("userId", "==", user.uid).get()
      .then((snap) => setIsEventStaff(!snap.empty))
      .catch(() => setIsEventStaff(false));
  }, [user]);

  const isOwner = user?.email === OWNER_EMAIL;

  // When the owner is impersonating, override the effective roles
  const effectiveIsAdmin         = isOwner && viewAs ? viewAs === "admin"       : isAdmin;
  const effectiveIsDeliveryStaff = isOwner && viewAs ? viewAs === "delivery"    : isDeliveryStaff;
  const effectiveIsEventStaff    = isOwner && viewAs ? viewAs === "event_staff" : isEventStaff;

  const openSignIn = (callback) => {
    afterSignInRef.current = callback || null;
    setSignInOpen(true);
  };

  const handleSuccess = () => {
    sessionStorage.removeItem("gvs_after_signin_path");
    setSignInOpen(false);
    if (afterSignInRef.current) {
      afterSignInRef.current();
      afterSignInRef.current = null;
    }
  };

  const closeSignIn = () => {
    sessionStorage.removeItem("gvs_after_signin_path");
    afterSignInRef.current = null;
    setSignInOpen(false);
  };

  const signOut = () => auth.signOut();

  return (
    <AuthContext.Provider value={{
      user,
      loading:         user === undefined,
      isAdmin:         effectiveIsAdmin,
      isDeliveryStaff: effectiveIsDeliveryStaff,
      isEventStaff:    effectiveIsEventStaff,
      isOwner,
      viewAs,
      setViewAs,
      openSignIn,
      signInWithGoogle,
      signOut,
    }}>
      {children}
      {signInOpen && (
        <SignInModal onClose={closeSignIn} onSuccess={handleSuccess} />
      )}
      {isLocalhost && user === null && !signInOpen && (
        <button
          onClick={handleDevSignIn}
          disabled={devSigningIn}
          style={{
            position: "fixed", bottom: 16, right: 16, zIndex: 9999,
            background: "#1a1a1a", color: "#fff", border: "none",
            borderRadius: 8, padding: "10px 16px", fontSize: 12,
            fontFamily: "inherit", cursor: "pointer", boxShadow: "0 2px 10px rgba(0,0,0,.25)",
          }}
        >
          {devSigningIn ? "Signing in…" : "🔧 Sign in as Admin (dev)"}
        </button>
      )}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
